import { type ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./auth-context";
import { authClient } from "../lib/auth-client";
import { Button, Clouds } from "../components/ui";
import type { UserRole } from "@app/shared";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2">
      <span className="text-2xl">🎒</span>
      <span className="text-lg font-extrabold text-brand-dark">とまこまい職場体験マッチング</span>
    </Link>
  );
}

const NAV: Record<UserRole, { to: string; label: string }[]> = {
  school: [
    { to: "/app/school/search", label: "企業を探す" },
    { to: "/app/school/applications", label: "申込一覧" },
  ],
  company: [
    { to: "/app/company/profile", label: "受入情報" },
    { to: "/app/company/inbox", label: "申込を確認" },
  ],
  admin: [
    { to: "/app/admin", label: "ダッシュボード" },
    { to: "/app/admin/reviews", label: "企業審査" },
    { to: "/app/admin/companies/new", label: "企業を代理登録" },
    { to: "/app/admin/schools", label: "学校アカウント" },
    { to: "/app/admin/industries", label: "業種マスタ" },
  ],
};

export function AppLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function signOut() {
    await authClient.signOut();
    await qc.invalidateQueries();
    navigate("/login");
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 border-b border-sky-base/60 bg-sky-soft/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <Logo />
          <nav className="hidden items-center gap-1 md:flex">
            {user &&
              NAV[user.role].map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end
                  className={({ isActive }) =>
                    `rounded-pill px-4 py-2 text-sm font-bold transition ${
                      isActive ? "bg-brand text-white" : "text-brand-dark hover:bg-white"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
          </nav>
          <div className="flex items-center gap-2">
            {user && (
              <NavLink
                to="/app/security"
                className="hidden text-xs font-bold text-brand-dark hover:underline sm:inline"
                title="セキュリティ設定"
              >
                🔐 {user.name}
              </NavLink>
            )}
            <Button variant="ghost" onClick={signOut}>
              ログアウト
            </Button>
          </div>
        </div>
        {/* モバイル用ナビ */}
        <nav className="flex gap-1 overflow-x-auto px-4 pb-2 md:hidden">
          {user &&
            NAV[user.role].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-pill px-3 py-1.5 text-xs font-bold ${
                    isActive ? "bg-brand text-white" : "bg-white text-brand-dark"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
        </nav>
      </header>
      <main className="relative mx-auto max-w-5xl px-4 py-8">
        <Clouds />
        {children}
      </main>
    </div>
  );
}

export function PublicLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return (
    <div className="min-h-screen">
      <header className="border-b border-sky-base/60 bg-sky-soft/80">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Logo />
          <Link to={user ? "/app" : "/login"}>
            <Button variant="ghost">{user ? "マイページ" : "ログイン"}</Button>
          </Link>
        </div>
      </header>
      <main className="relative mx-auto max-w-5xl px-4 py-8">
        <Clouds />
        {children}
      </main>
      <footer className="mt-12 py-8 text-center text-xs text-slate-400">
        苫小牧タウンマネジメント / 中学校職場体験マッチング
      </footer>
    </div>
  );
}
